/**
 * Scrubs credentials out of anything that leaves the browser as telemetry: Sentry events,
 * breadcrumbs and transactions, and PostHog event properties.
 */

// Bearer tokens (DRF `Token <key>` and impersonation `imp_…` keys) ride in
// AxiosError.config.headers.Authorization, which captureConsoleIntegration can
// export to Sentry.
const TOKEN_PATTERN = /\b(?:Token\s+|imp_)[A-Za-z0-9]+/g;

// Secrets that ride in URLs: the onboarding magic link (`?access=`) and the bearer token the mobile
// WebView and password-reset mails arrive with (`?token=`). index.html strips both before any SDK
// loads, but Navigation Timing keeps the original URL and browser-metrics spans report it.
const URL_SECRET_NAMES: ReadonlySet<string> = new Set(["access", "token"]);

// Every `name=value` query pair, in a bare URL or embedded in free text (a span description, a log
// line): at the start of the string (a bare query string) or after `?`/`&`. The value runs to the
// next `&`, `#` or whitespace.
const QUERY_PAIR_PATTERN = /(^|[?&])([^=&#?\s]+)=([^&#\s]*)/g;

/**
 * Decode a raw query parameter name exactly as `URLSearchParams` does (so `%74oken` and `to%6Ben`
 * are `token`, as the handoff parser in index.html sees them), a few times over in case it was
 * double-encoded, then lower-case it.
 */
const decodeParamName = (raw: string): string => {
  let name = raw;
  for (let i = 0; i < 3; i++) {
    const decoded = new URLSearchParams(`${name}=`).keys().next().value ?? name;
    if (decoded === name) break;
    name = decoded;
  }
  return name.toLowerCase();
};

/** Redact the value of every `?token=` / `?access=` pair, whatever percent-encoding the name uses. */
const redactUrlSecrets = (text: string): string =>
  text.replace(QUERY_PAIR_PATTERN, (pair, separator: string, name: string, secret: string) =>
    secret && URL_SECRET_NAMES.has(decodeParamName(name)) ? `${separator}${name}=[redacted]` : pair,
  );

/** Redact every credential-shaped string (and any `authorization` key) in a telemetry payload. */
export const redactSecrets = (value: unknown, depth = 0): unknown => {
  if (value == null || depth > 6) return value;
  if (typeof value === "string") {
    return redactUrlSecrets(value.replace(TOKEN_PATTERN, "[redacted]"));
  }
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = /authorization/i.test(k) ? "[redacted]" : redactSecrets(v, depth + 1);
    }
    return out;
  }
  return value;
};
